'use client';

import React, { useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z',
    title: 'Get a Quote',
    desc: 'Tell us where you\'re shipping from, where it\'s going, and what you\'re sending. We\'ll respond within 2 hours.',
    color: '#1B6B8A',
    colorBg: 'rgba(27,107,138,0.08)',
    colorBorder: 'rgba(27,107,138,0.2)',
  },
  {
    number: '02',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0 1 12 2.944a11.955 11.955 0 0 1-8.618 3.04A12.02 12.02 0 0 0 3 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    title: 'We Handle It',
    desc: 'Our team manages everything — booking, customs clearance, documentation, and coordination across all modes of transport.',
    color: '#2A8FAB',
    colorBg: 'rgba(42,143,171,0.08)',
    colorBorder: 'rgba(42,143,171,0.2)',
  },
  {
    number: '03',
    icon: 'M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7',
    title: 'Track in Portal',
    desc: 'Log in to MY WTO to track your shipment live, download documents, and stay updated at every milestone — 24/7.',
    color: '#1B6B8A',
    colorBg: 'rgba(27,107,138,0.06)',
    colorBorder: 'rgba(27,107,138,0.18)',
  },
];

const HowItWorksSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = sectionRef.current?.querySelectorAll('.reveal-step');
    if (!els) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-16 lg:py-20 overflow-hidden"
      style={{ background: '#F4F6F9' }}
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(27,107,138,0.3), transparent)' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-4" style={{ borderColor: 'rgba(27,107,138,0.25)', background: 'rgba(27,107,138,0.07)' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#1B6B8A' }} />
            <span className="text-xs font-medium font-body tracking-widest uppercase" style={{ color: '#1B6B8A' }}>
              How It Works
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: '#0F2340' }}>
            Shipping made <span className="italic" style={{ color: '#1B6B8A' }}>simple</span>
          </h2>
          <p className="font-body text-sm mt-2 max-w-md mx-auto" style={{ color: '#3A5070' }}>
            Three steps from enquiry to delivery.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {/* Connector line (desktop) */}
          <div
            className="hidden md:block absolute top-10 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-px pointer-events-none"
            style={{ background: 'linear-gradient(90deg, rgba(27,107,138,0.25), rgba(42,143,171,0.2), rgba(27,107,138,0.2))' }}
          />

          {steps.map((step, i) => (
            <div
              key={step.number}
              className="reveal-step relative flex flex-col items-center text-center p-6 rounded-2xl transition-all duration-500"
              style={{
                background: '#FFFFFF',
                border: `1px solid ${step.colorBorder}`,
                transitionDelay: `${i * 120}ms`,
                opacity: 0,
                transform: 'translateY(20px)',
                boxShadow: '0 2px 16px rgba(15,35,64,0.08)',
              }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-4 relative z-10"
                style={{ background: step.colorBg, border: `1.5px solid ${step.colorBorder}` }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={step.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d={step.icon} />
                </svg>
              </div>

              <span className="text-xs font-body font-semibold tracking-widest uppercase mb-2" style={{ color: step.color }}>
                Step {step.number}
              </span>

              <h3 className="font-display text-xl mb-2" style={{ color: '#0F2340' }}>{step.title}</h3>
              <p className="text-sm font-body leading-relaxed" style={{ color: '#3A5070' }}>{step.desc}</p>

              {i < steps.length - 1 && (
                <div className="md:hidden mt-4" style={{ color: 'rgba(15,35,64,0.2)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12l7 7 7-7" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* First-time shipper helper + CTA */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* First-time shipper card */}
          <div className="rounded-2xl p-6 border flex gap-4 items-start" style={{ background: '#FFFFFF', borderColor: 'rgba(27,107,138,0.15)', boxShadow: '0 2px 12px rgba(15,35,64,0.07)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(27,107,138,0.08)', border: '1px solid rgba(27,107,138,0.2)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1B6B8A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <path d="M12 17h.01" />
              </svg>
            </div>
            <div>
              <p className="font-display text-base mb-1" style={{ color: '#0F2340' }}>First time shipping?</p>
              <p className="text-sm font-body leading-relaxed mb-3" style={{ color: '#3A5070' }}>
                Not sure which service fits your shipment? Our team will guide you through every step — from choosing the right mode to customs clearance.
              </p>
              <a
                href="/contacts"
                className="inline-flex items-center gap-1.5 text-sm font-medium font-body transition-colors duration-200"
                style={{ color: '#1B6B8A' }}>
                Talk to a specialist
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>

          {/* Get a quote CTA */}
          <div className="rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ background: 'linear-gradient(135deg, #0F2340 0%, #1A3558 100%)', border: '1px solid rgba(27,107,138,0.3)' }}>
            <div>
              <p className="font-display text-base mb-1" style={{ color: '#FFFFFF' }}>Ready to move your cargo?</p>
              <p className="text-sm font-body" style={{ color: 'rgba(255,255,255,0.7)' }}>Get a competitive freight quote in minutes.</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <a
                href="/transport-quote"
                className="px-5 py-2.5 rounded-full text-sm font-semibold font-body transition-all duration-200 hover:opacity-90"
                style={{ background: '#1A5FAB', color: '#ffffff' }}>
                Get a Quote
              </a>
              <a
                href="/contacts"
                className="px-5 py-2.5 rounded-full text-sm font-medium font-body transition-all duration-200"
                style={{ border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}>
                Contact Us
              </a>
            </div>
          </div>
        </div>

        {/* CTA below steps */}
        <div className="text-center mt-10">
          <p className="text-xs font-body mt-2.5" style={{ color: '#6A85A0' }}>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#5BBFA0' }} />
              We respond within <span className="font-semibold" style={{ color: '#5BBFA0' }}>2 hours</span> — Monday to Friday
            </span>
          </p>
        </div>
      </div>

      <style>{`
        .reveal-step.visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
};

export default HowItWorksSection;
